import type { StateCreator } from 'zustand'
import type { IdsFile } from '@modules/ids/lib/idsParse'
import type { IdsReport } from '@modules/ids/lib/idsEngine'
import type { IdsxReport } from '@modules/ids/lib/idsxEngine'
import type { ModelSuggestions } from '@modules/ids/lib/idsSuggest'

// Estado do módulo IDS, tirado do `useState` do `useIdsValidation` /
// `useIdsxRun` para o store global. Motivo (o mesmo bug da Tabela no
// beta.76): a casca do workspace desmonta a página do módulo sempre que
// se sai dela, e com o estado local isso deitava fora o relatório de
// validação inteiro, o ficheiro .ids carregado e as sugestões lidas do
// modelo. Voltar à página obrigava a abrir o .ids outra vez e a
// revalidar, que em modelos grandes não é de graça.
//
// PORQUE NÃO SE JUNTOU AO `viewer/store/idsSlice.ts`: aquele slice é
// cromo do viewer (o toggle do painel flutuante no ribbon e o canal de
// pré-preenchimento do deep link), vive em `viewer/store/` e é limpo
// pelo "Fechar sessão" como os outros painéis. Este é estado de domínio
// do módulo e vive em `modules/ids/store/`, como o BoQ, o IFCEdit e a
// Tabela. Como ambos são achatados no mesmo `useAppStore`, o painel
// flutuante e a página lêem exactamente o mesmo estado de qualquer
// maneira: a separação é de arrumação, não de conteúdo.
//
// O PAINEL E A PÁGINA PARTILHAM TUDO O QUE ESTÁ AQUI. É o mesmo motor
// sobre o mesmo modelo: validar no painel e abrir a página tem de
// mostrar o relatório, e vice-versa. Antes eram duas instâncias do hook
// com dois `useState` independentes, e o mesmo trabalho aparecia feito
// num sítio e por fazer no outro.
//
// PERSISTÊNCIA (regra do beta.75): nada disto vai para localStorage. O
// relatório é DERIVADO (IFC + ficheiro .ids), não é trabalho autoral:
// repô-lo depois de um reload seria exactamente o "trabalho que
// reaparece sozinho" que a rede de recuperação proíbe. Os rascunhos dos
// editores ("Criar IDS" e "Regras") são trabalho e continuam onde
// estavam, em localStorage e no manifesto de recuperação.

/** Separador activo da página do módulo. */
export type IdsTab = 'validar' | 'criar' | 'regras'

/**
 * Assinatura dos modelos carregados. Serve de teste de validade: se a
 * assinatura corrente não bater certo com a que está no store, o
 * relatório em memória foi produzido por outra combinação de IFCs e tem
 * de ir abaixo (é isto que impede resultados fantasma de um modelo já
 * descarregado).
 */
export function idsModelKey(models: { id: string; fileName: string }[]): string {
  return models.map((m) => `${m.id} ${m.fileName}`).join('\n')
}

export interface IdsModuleSlice {
  /** Separador da página. Sobrevive porque o relatório também sobrevive:
   *  correr as regras e voltar a cair em "Validar" era desnorteante. */
  idsTab: IdsTab
  setIdsTab: (t: IdsTab) => void

  // --- Comum aos dois motores -----------------------------------------

  /** Assinatura dos modelos que produziram o que está em memória. */
  idsModelKey: string
  /** Aviso "os modelos mudaram e o relatório foi limpo". */
  idsStale: boolean
  /** Sugestões lidas do IFC (classes, psets, propriedades), por modelo.
   *  Caras: obrigam a varrer o modelo. Antes viviam em `useState` de
   *  cada editor e perdiam-se a cada saída da página. */
  idsSuggestions: Record<string, ModelSuggestions>
  idsSugBusy: boolean

  // --- Validar (IDS standard) -----------------------------------------

  /** Ficheiro .ids carregado, já em árvore. NÃO é derivado do IFC: uma
   *  troca de modelo não o leva, para se poder revalidar o mesmo IDS
   *  contra o modelo novo. */
  idsFile: IdsFile | null
  idsFileName: string
  /** Escolha de modelo do utilizador (o efectivo resolve-se no hook). */
  idsModelSel: string
  idsReport: IdsReport | null
  /** Modelo que produziu `idsReport`. A lista de specs usa este, não o
   *  seleccionado: trocar o selector depois de validar não pode passar a
   *  seleccionar elementos do modelo errado. */
  idsReportModelId: string
  idsBusy: boolean
  idsStep: string | null
  idsError: string | null
  idsShow3d: boolean
  idsBcfMsg: string | null
  idsExpandedSpecs: Set<number>
  /** Coluna de resultados aberta no editor "Criar IDS". */
  idsResultsOpen: boolean
  setIdsResultsOpen: (open: boolean) => void

  // --- Regras (extensão VN2R, .idsx) ----------------------------------

  idsxModelSel: string
  idsxReport: IdsxReport | null
  idsxReportModelId: string
  /** Nome do ficheiro de regras que correu; só serve para nomear as
   *  exportações. */
  idsxTitle: string
  idsxBusy: boolean
  idsxStep: string | null
  idsxError: string | null
  idsxShow3d: boolean
  idsxBcfMsg: string | null
  idsxExpandedRules: Set<number>
  /** Coluna de resultados aberta na página das regras. */
  idsxResultsOpen: boolean
  setIdsxResultsOpen: (open: boolean) => void

  // --- Acções ----------------------------------------------------------

  /** Adopta o ficheiro .ids (ou nenhum) e larga o relatório anterior, que
   *  era de outra especificação. */
  setIdsFile: (file: IdsFile | null, name: string) => void
  setIdsModelSel: (id: string) => void
  /** Começou a validar: limpa o que a corrida anterior deixou. */
  startIdsValidation: () => void
  finishIdsValidation: (report: IdsReport, modelId: string) => void
  failIdsValidation: (msg: string) => void
  setIdsStep: (s: string | null) => void
  setIdsError: (e: string | null) => void
  setIdsShow3d: (on: boolean) => void
  setIdsBcfMsg: (m: string | null) => void
  toggleIdsSpec: (i: number) => void

  setIdsxModelSel: (id: string) => void
  startIdsxRun: (titulo: string) => void
  finishIdsxRun: (
    report: IdsxReport,
    modelId: string,
    expanded: Set<number>,
  ) => void
  failIdsxRun: (msg: string) => void
  setIdsxStep: (s: string | null) => void
  setIdsxShow3d: (on: boolean) => void
  setIdsxBcfMsg: (m: string | null) => void
  toggleIdsxRule: (i: number) => void

  setIdsSuggestions: (modelId: string, s: ModelSuggestions) => void
  setIdsSugBusy: (busy: boolean) => void

  /**
   * Os modelos carregados mudaram. Larga tudo o que era derivado deles
   * (os dois relatórios e as sugestões dos modelos que já não existem) e
   * levanta o aviso se havia alguma coisa a perder.
   */
  syncIdsModels: (key: string, liveModelIds: string[]) => void
  dismissIdsStale: () => void
  /** Lousa limpa. Chamado pelo "Fechar sessão". */
  resetIdsModule: () => void
}

/** Tudo o que é derivado dos modelos. Uma troca de IFCs leva isto. */
function derivedInitialState() {
  return {
    idsReport: null,
    idsReportModelId: '',
    idsBusy: false,
    idsStep: null,
    idsError: null,
    idsBcfMsg: null,
    idsExpandedSpecs: new Set<number>(),
    idsxReport: null,
    idsxReportModelId: '',
    idsxBusy: false,
    idsxStep: null,
    idsxError: null,
    idsxBcfMsg: null,
    idsxExpandedRules: new Set<number>(),
  }
}

export const createIdsModuleSlice: StateCreator<IdsModuleSlice> = (set) => ({
  idsTab: 'validar',
  idsModelKey: '',
  idsStale: false,
  idsSuggestions: {},
  idsSugBusy: false,
  idsFile: null,
  idsFileName: '',
  idsModelSel: '',
  idsShow3d: true,
  idsResultsOpen: false,
  idsxModelSel: '',
  idsxTitle: 'regras',
  idsxShow3d: true,
  idsxResultsOpen: false,
  ...derivedInitialState(),

  setIdsTab: (t) => set({ idsTab: t }),
  setIdsResultsOpen: (open) => set({ idsResultsOpen: open }),
  setIdsxResultsOpen: (open) => set({ idsxResultsOpen: open }),

  setIdsFile: (file, name) =>
    set({
      idsFile: file,
      idsFileName: name,
      idsReport: null,
      idsReportModelId: '',
      idsError: null,
      idsBcfMsg: null,
      idsExpandedSpecs: new Set<number>(),
    }),

  setIdsModelSel: (id) => set({ idsModelSel: id }),

  startIdsValidation: () =>
    set({
      idsBusy: true,
      idsError: null,
      idsReport: null,
      idsReportModelId: '',
      idsBcfMsg: null,
      idsExpandedSpecs: new Set<number>(),
      idsResultsOpen: true,
    }),

  // Relatório novo por cima: o aviso de invalidação já não tem assunto.
  finishIdsValidation: (report, modelId) =>
    set({
      idsReport: report,
      idsReportModelId: modelId,
      idsBusy: false,
      idsStep: null,
      idsStale: false,
    }),

  failIdsValidation: (msg) =>
    set({ idsError: msg, idsBusy: false, idsStep: null }),

  setIdsStep: (s) => set({ idsStep: s }),
  setIdsError: (e) => set({ idsError: e }),
  setIdsShow3d: (on) => set({ idsShow3d: on }),
  setIdsBcfMsg: (m) => set({ idsBcfMsg: m }),

  toggleIdsSpec: (i) =>
    set((s) => {
      const next = new Set(s.idsExpandedSpecs)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return { idsExpandedSpecs: next }
    }),

  setIdsxModelSel: (id) => set({ idsxModelSel: id }),

  startIdsxRun: (titulo) =>
    set({
      idsxTitle: titulo,
      idsxBusy: true,
      idsxError: null,
      idsxReport: null,
      idsxReportModelId: '',
      idsxBcfMsg: null,
      idsxExpandedRules: new Set<number>(),
      idsxResultsOpen: true,
    }),

  finishIdsxRun: (report, modelId, expanded) =>
    set({
      idsxReport: report,
      idsxReportModelId: modelId,
      idsxExpandedRules: expanded,
      idsxBusy: false,
      idsxStep: null,
      idsStale: false,
    }),

  failIdsxRun: (msg) =>
    set({ idsxError: msg, idsxBusy: false, idsxStep: null }),

  setIdsxStep: (s) => set({ idsxStep: s }),
  setIdsxShow3d: (on) => set({ idsxShow3d: on }),
  setIdsxBcfMsg: (m) => set({ idsxBcfMsg: m }),

  toggleIdsxRule: (i) =>
    set((s) => {
      const next = new Set(s.idsxExpandedRules)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return { idsxExpandedRules: next }
    }),

  setIdsSuggestions: (modelId, sug) =>
    set((s) => ({ idsSuggestions: { ...s.idsSuggestions, [modelId]: sug } })),

  setIdsSugBusy: (busy) => set({ idsSugBusy: busy }),

  syncIdsModels: (key, liveModelIds) =>
    set((s) => {
      const live = new Set(liveModelIds)
      const sug: Record<string, ModelSuggestions> = {}
      for (const [id, v] of Object.entries(s.idsSuggestions)) {
        if (live.has(id)) sug[id] = v
      }
      return {
        ...derivedInitialState(),
        idsModelKey: key,
        idsSuggestions: sug,
        // Só se avisa quem perdeu alguma coisa: sem relatório não houve
        // nada a invalidar. O aviso é pegajoso porque trocar de IFC
        // passa por descarregar o velho e carregar o novo: são duas
        // trocas seguidas, e só a primeira é que ainda vê o relatório.
        // Apaga-se ao validar de novo ou ao dispensá-lo.
        idsStale: s.idsStale || s.idsReport !== null || s.idsxReport !== null,
      }
    }),

  dismissIdsStale: () => set({ idsStale: false }),

  resetIdsModule: () =>
    set({
      ...derivedInitialState(),
      idsTab: 'validar',
      idsModelKey: '',
      idsStale: false,
      idsSuggestions: {},
      idsSugBusy: false,
      idsFile: null,
      idsFileName: '',
      idsModelSel: '',
      idsShow3d: true,
      idsResultsOpen: false,
      idsxModelSel: '',
      idsxTitle: 'regras',
      idsxShow3d: true,
      idsxResultsOpen: false,
    }),
})
